import { Temporal } from '@js-temporal/polyfill';

/**
 * Checks whether a given string is a valid ISO 8601 datetime string that represents a UTC time (ending with 'Z' or 'z').
 *
 * This function validates if the input is a properly structured ISO 8601 datetime using `Temporal.Instant.from`.
 *
 * ⚡ Important:
 * - Only datetime strings ending with a UTC designator ('Z' or 'z') are considered valid.
 * - Strings containing timezone offsets (e.g., '-05:00', '+09:00') are **not** treated as valid ISO datetime strings by this check.
 * - This check does not allow "local" datetimes (without timezone information) either.
 *
 * @param {string} value - The string to validate as an ISO 8601 UTC datetime.
 * @returns {boolean} Returns `true` if the string is valid and explicitly UTC ('Z'), otherwise `false`.
 *
 * @example
 * isIsoDate('2024-06-18T12:00:00Z'); // true
 * isIsoDate('2024-06-18T12:00:00z'); // true
 * isIsoDate('2024-06-18T12:00:00-05:00'); // false (offset not allowed)
 * isIsoDate('2024-06-18T12:00:00'); // false (missing timezone information)
 * isIsoDate('invalid-date'); // false
 */
export const isIsoDate = (value: string): boolean => {
  try {
    Temporal.Instant.from(value); // throws if not valid ISO 8601
    return true;
  } catch {
    return false;
  }
};
